import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import { onAuthStateChanged, signOut, User } from "firebase/auth";
import { auth } from "../firebase";
import axios from "axios";

interface AuthContextType {
  user: User | null;
  loading: boolean;
  profile: any | null;
  /**
   * True from a signed-in user appearing until their profile has arrived (or
   * the fetch has failed). `permissions` rides on that profile, so the navbar
   * cannot know which menus to draw until it lands.
   */
  profileLoading: boolean;
  oauthAccessToken: string | null;
  setOauthAccessToken: (token: string | null) => void;
  ensureSession: (u?: User) => Promise<boolean>;
  /** Ask for the sign-in code. Returns what to show next. */
  requestLoginOtp: (u: User, opts?: { resend?: boolean; challengeId?: string }) => Promise<LoginOtpChallenge>;
  /** Exchange password + code for a session. Surfaces why a code was refused. */
  createSessionWithOtp: (u: User, otp: { challengeId: string; code: string }) => Promise<SessionResult>;
  emailVerified: boolean | null;
  refreshEmailVerified: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  loading: true,
  profile: null,
  profileLoading: false,
  oauthAccessToken: null,
  setOauthAccessToken: (_token: string | null) => {},
  ensureSession: async (_u?: User) => false,
  requestLoginOtp: async () => ({ otpRequired: false }),
  createSessionWithOtp: async () => ({ ok: false }),
  emailVerified: null,
  refreshEmailVerified: async () => {},
});

/**
 * Set when the server reports that a branch is needed, cleared once one is
 * chosen — or as soon as the server says it is no longer needed.
 *
 * The server decides this per working day, not per call. It used to flag every
 * staff sign-in, and since the browser re-mints its session on each page reload,
 * the prompt came back on every refresh.
 *
 * Kept in sessionStorage so it survives a page reload but not a new
 * browser session — which matches "asked at each login" closely enough, and the
 * branch is a record of where a sale happened rather than a security control.
 */
export interface LoginOtpChallenge {
  otpRequired: boolean;
  challengeId?: string;
  /** Masked, e.g. "+971 ••• 233". Enough to know which handset, not the number. */
  sentTo?: string;
  resendInSeconds?: number;
}

export interface SessionResult {
  ok: boolean;
  /** True when the code was wrong, expired or used up — not a network failure. */
  otpFailed?: boolean;
  message?: string;
  attemptsLeft?: number;
}

export const BRANCH_PROMPT_KEY = "bt.branchPromptPending";

export function isBranchPromptPending(): boolean {
  return sessionStorage.getItem(BRANCH_PROMPT_KEY) === "1";
}

export function clearBranchPrompt(): void {
  sessionStorage.removeItem(BRANCH_PROMPT_KEY);
}

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState<any | null>(null);
  const [profileLoading, setProfileLoading] = useState(false);
  const [oauthAccessToken, setOauthAccessToken] = useState<string | null>(null);
  const [sessionCreated, setSessionCreated] = useState(false);
  const [emailVerified, setEmailVerified] = useState<boolean | null>(null);

  const creatingRef = useRef<Promise<boolean> | null>(null);

  /**
   * True from the moment a sign-in starts until it finishes.
   *
   * Firebase accepts the password before the code is entered, so `user` is set
   * while the login screen is still waiting on the second step. Without this
   * flag the automatic restore below fires at that exact moment, is told a code
   * is required, and signs the person out — halfway through signing them in,
   * taking the login screen's user object with it. The code box never gets its
   * chance and the password appears to be rejected.
   */
  const interactiveLoginRef = useRef(false);

  /**
   * Step one of signing in: the password is already checked, this sends the
   * code. No session cookie is issued here — until the code comes back the
   * browser holds nothing.
   */
  const requestLoginOtp = useCallback(
    async (u: User, opts?: { resend?: boolean; challengeId?: string }): Promise<LoginOtpChallenge> => {
      // A sign-in is under way. Hold off the automatic restore until it ends.
      interactiveLoginRef.current = true;
      const idToken = await u.getIdToken(false);
      const res = await axios.post(
        "/api/loginOtp",
        { idToken, resend: opts?.resend ?? false, challengeId: opts?.challengeId },
        { withCredentials: true },
      );
      // Customers need no code, so there is no second step to wait for and the
      // flag must come back down — leaving it raised would block the automatic
      // restore for the rest of this page's life.
      if (res.data?.otpRequired !== true) {
        interactiveLoginRef.current = false;
      }
      return res.data;
    },
    [],
  );

  /**
   * Step two: password plus code for a session.
   *
   * Kept separate from ensureSession because a refused code is not a failure to
   * report as "server unavailable" — the person needs to know the code was
   * wrong and how many tries remain.
   */
  const createSessionWithOtp = useCallback(
    async (u: User, otp: { challengeId: string; code: string }): Promise<SessionResult> => {
      try {
        const idToken = await u.getIdToken(false);
        const res = await axios.post(
          "/api/sessionLogin",
          { idToken, challengeId: otp.challengeId, code: otp.code },
          { withCredentials: true },
        );
        interactiveLoginRef.current = false;
        setSessionCreated(true);
        if (res.data?.requiresBranchSelection) {
          sessionStorage.setItem(BRANCH_PROMPT_KEY, "1");
        } else {
          sessionStorage.removeItem(BRANCH_PROMPT_KEY);
        }
        return { ok: true };
      } catch (err: any) {
        const data = err?.response?.data;
        // A wrong code leaves the flow open — the person tries again. Anything
        // else has ended it, so release the flag rather than blocking the
        // restore for the rest of the page's life.
        if (data?.error === "otp_failed") {
          return { ok: false, otpFailed: true, message: data.message, attemptsLeft: data.attemptsLeft };
        }
        interactiveLoginRef.current = false;
        return { ok: false, message: data?.message ?? data?.error ?? "Could not sign you in." };
      }
    },
    [],
  );

  const ensureSession = useCallback(async (u?: User): Promise<boolean> => {
    const target = u || user;
    if (!target) return false;

    // Was this an interactive sign-in, or the automatic restore on page load?
    //
    // The login screen passes the user it just authenticated; the restore
    // effect passes nothing and falls back to state. Only the second kind may
    // end the Firebase session on a refusal — doing it during a sign-in would
    // sign somebody out midway through signing them in, and the login screen
    // needs the user object to collect the code.
    const isAutomaticRestore = u === undefined;

    // If a call is already in flight, wait for its result instead of
    // starting a duplicate (and having one caller lose the race).
    if (creatingRef.current) {
      return creatingRef.current;
    }

    const attempt = async (forceRefresh: boolean) => {
      const idToken = await target.getIdToken(forceRefresh);
      return axios.post("/api/sessionLogin", { idToken }, { withCredentials: true });
    };

    const promise = (async (): Promise<boolean> => {
      try {
        let res;
        try {
          res = await attempt(false);
        } catch (first: any) {
          // Firebase hands back a cached id token, which may have been minted
          // before the account's tokens were revoked — and revocation is a
          // normal event: signing out elsewhere, or a super user disabling
          // somebody. A session cookie built from such a token is refused the
          // moment it is checked, so the browser sits in a loop that a reload
          // cannot break.
          //
          // Force one refresh and try again. If the tokens really are revoked
          // the refresh itself throws, which is the answer we want — it means
          // this person has to sign in again, and the catch below sees them to
          // the login screen.
          if (first?.response?.status !== 401 || !isAutomaticRestore) throw first;
          res = await attempt(true);
        }
        setSessionCreated(true);

        // Staff rotate between branches and pick one after each sign-in. This
        // call happens exactly once per login, so it is the right place to
        // raise the flag — inferring "is this a new login?" anywhere else means
        // guessing.
        // Follow the server's answer both ways. It decides this per working
        // day, so a session re-minted on a page reload reports false once the
        // branch has been chosen — and clearing on false means a tab holding a
        // stale flag stops asking too.
        if (res.data?.requiresBranchSelection) {
          sessionStorage.setItem(BRANCH_PROMPT_KEY, "1");
        } else {
          sessionStorage.removeItem(BRANCH_PROMPT_KEY);
        }

        console.log("[Auth] Session created");
        return true;
      } catch (error: any) {
        const status = error?.response?.status;
        console.error("[Auth] Session creation failed:", error.message);

        // A 401 means the server will not grant a session for this browser —
        // most often because sign-in needs a one-time code that only the login
        // screen can collect. Firebase keeps its own copy of the signed-in user
        // in local storage, so without this the app sits in a state where it
        // looks signed in, holds no session, shows no menus, and offers nothing
        // to press. Ending the Firebase session makes the UI tell the truth and
        // puts the login screen back, where the code can actually be entered.
        //
        // Two ways to land here and mean "this person must sign in again": the
        // server refused (401), or Firebase itself would not renew the token
        // (auth/user-token-expired, auth/user-disabled, auth/invalid-user-token
        // — all of which mean the account's tokens were revoked).
        //
        // Only for the automatic restore. A network blip or a 429 is not a
        // statement about who this person is, and signing them out over one
        // would be its own bug.
        const firebaseRejected = typeof error?.code === "string" && error.code.startsWith("auth/");

        // The flag is read here rather than when the call started. The restore
        // effect and the login screen both react to the same `user` appearing,
        // so which runs first is not guaranteed — but by the time a request has
        // been to the server and back, a sign-in that was starting has
        // certainly claimed it. Checking late is what makes that race harmless.
        if (isAutomaticRestore && !interactiveLoginRef.current && (status === 401 || firebaseRejected)) {
          try {
            await signOut(auth);
          } catch {
            // Already gone, or offline. onAuthStateChanged settles it either way.
          }
        }
        return false;
      } finally {
        creatingRef.current = null;
      }
    })();

    creatingRef.current = promise;
    return promise;
  }, [user]);

  // Firebase auth state listener
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => {
      setUser(u);
      setLoading(false);
      if (!u) {
        setSessionCreated(false);
        setProfile(null);
        setOauthAccessToken(null);
        creatingRef.current = null;
        interactiveLoginRef.current = false;

        // Clear oauthAccessToken cookie on sign-out
        const isProd = process.env.NODE_ENV === "production";
        document.cookie = [
          "oauthAccessToken=",
          "max-age=0",
          "path=/",
          isProd ? "secure" : "",
          isProd ? "samesite=none" : "samesite=lax",
          ...(isProd ? [] : ["domain=localhost"]),
        ]
          .filter(Boolean)
          .join("; ");
      } else {
        // New user — need a fresh session
        setSessionCreated(false);
        setProfile(null);
      }
    });
    return unsub;
  }, []);

  // Create session when user appears without one
  useEffect(() => {
    // Not on the sign-in screens. Those own the flow — they authenticate, ask
    // for the code, and create the session themselves. Restoring in parallel
    // there races them: Firebase sets `user` the moment the password is
    // accepted, so this fires while the code box is still being filled in,
    // sends a request that cannot succeed, and puts a 401 in the network log
    // of the exact page someone is most likely to be debugging.
    //
    // Read from the address bar rather than a router hook, so the provider
    // stays usable outside a <Router> and needs no props from its caller.
    const onSignInScreen = /^\/(login|signup)\b/.test(window.location.pathname);

    if (user && !sessionCreated && !interactiveLoginRef.current && !onSignInScreen) {
      ensureSession();
    }
  }, [user, sessionCreated, ensureSession]);

  const refreshEmailVerified = useCallback(async () => {
    if (!user) {
      setEmailVerified(null);
      return;
    }
    try {
      const res = await axios.get("/api/check-email-verified", {
        withCredentials: true,
      });
      setEmailVerified(res.data.emailVerified === true);
    } catch (err: any) {
      // Don't block on failure — server middleware is the real gate.
      setEmailVerified(null);

      // Except when the server says the credential itself is dead. Firebase
      // keeps its own record of the signed-in user, so otherwise the app sits
      // there looking signed in while every request is refused, and nothing
      // ever clears it. Ending the Firebase session puts the login screen back.
      //
      // Never while a sign-in is under way: this request can be a straggler
      // carrying the *previous*, dead cookie, arriving after a fresh session
      // has already been granted. Acting on it then would sign the person out
      // moments after letting them in.
      if (
        err?.response?.data?.reason === "session_invalid" &&
        !interactiveLoginRef.current &&
        !sessionCreated
      ) {
        try {
          await signOut(auth);
        } catch {
          // Already gone, or offline. onAuthStateChanged settles it either way.
        }
      }
    }
  }, [user, sessionCreated]);

  // Check email verification when session is ready
  useEffect(() => {
    if (user && sessionCreated) {
      refreshEmailVerified();
    } else if (!user) {
      setEmailVerified(null);
    }
  }, [user, sessionCreated, refreshEmailVerified]);

  // Fetch profile when session is ready
  useEffect(() => {
    if (!user) {
      setProfile(null);
      setProfileLoading(false);
      return;
    }
    if (!sessionCreated) {
      // Signed in, but the session cookie is still being minted, so the
      // profile request cannot go out yet. One is coming, so this counts as
      // loading — otherwise the navbar draws its signed-out shape for a moment
      // and then rearranges itself once the menus resolve.
      setProfile(null);
      setProfileLoading(true);
      return;
    }
    let cancelled = false;
    setProfileLoading(true);
    (async () => {
      try {
        // /profile/me, not /profile. nginx serves the bare path from the SPA
        // bundle and forwards only /profile/<something> to the API, so the old
        // call came back as a 200 carrying index.html — `profile` became an
        // HTML string, `permissions` was undefined on it, and every staff and
        // admin menu in the navbar disappeared for accounts that genuinely had
        // them. The uid goes with it: the server identifies the caller from the
        // session cookie and never read the query parameter.
        const res = await axios.get("/profile/me", { withCredentials: true });
        if (!cancelled) setProfile(res.data);
      } catch (error: any) {
        if (!cancelled) {
          console.error("[Auth] Profile fetch failed:", error.message);
          setProfile(null);
        }
      } finally {
        // Always, including on failure. Leaving it set would hold the navbar
        // on its placeholder for the rest of the page's life.
        if (!cancelled) setProfileLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [user, sessionCreated]);

  return (
    <AuthContext.Provider value={{ user, loading, profile, profileLoading, oauthAccessToken, setOauthAccessToken, ensureSession, requestLoginOtp, createSessionWithOtp, emailVerified, refreshEmailVerified }}>
      {children}
    </AuthContext.Provider>
  );
};

export function useAuth() {
  return useContext(AuthContext);
}
